import React, {useMemo, useRef, useState, useEffect} from "react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import SendIcon from "@mui/icons-material/Send";
import {useSelector} from "react-redux";
import {selectSessionUser} from "../../../../../../store/authentication/selectors";
import { alpha } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";

const PrivateQuestion = ({onBack, socket}) => {
    // Chat state
    const [messages, setMessages] = useState([]); // { id, text, author: 'me' }
    const [input, setInput] = useState("");
    const [isThinking, setIsThinking] = useState(false);
    const listRef = useRef(null);

    const canSend = useMemo(() => input.trim().length > 0, [input]);
    const user = useSelector(selectSessionUser);

    const handleSend = () => {
        if (!canSend) return;
        const msg = {
            id: Date.now(),
            text: input.trim(),
            author: "me",
        };
        setMessages((prev) => [...prev, msg]);
        setInput("");
        setIsThinking(true);
        socket.emit("private_question", {question: msg.text, playerId: user._id});
    };

    useEffect(() => {
        const handlePrivateAnswer = (data) => {
            setMessages((prev) => [
                ...prev,
                { text: data.answer, id: Date.now(), author: "nostra" },
            ]);
            setIsThinking(false);
        };

        // Register listener
        socket.on("private_answer", handlePrivateAnswer);

        // Cleanup to avoid duplicate listeners (e.g., React StrictMode)
        return () => {
            socket.off("private_answer", handlePrivateAnswer);
        };
    }, [socket]);

    // Auto-scroll to bottom when messages change or spinner appears
    useEffect(() => {
        const el = listRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages, isThinking]);

    return (
        <Box sx={{width: "100%", display: "flex", flexDirection: "column", gap: 2, height: "95vh", position: "relative"}}>
            {/* Back control consistent with GameRules */}
            <button
                aria-label="Retour"
                onClick={onBack}
                style={{
                    alignSelf: "flex-start",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    background: "transparent",
                    color: "#fff",
                    border: "none",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: "pointer",
                    padding: 8,
                    borderRadius: 8,
                }}
            >
                <ArrowBackIosNewIcon fontSize="small"/>
                Retour
            </button>

            {/* Messages list */}
            <Box
                ref={listRef}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    padding: 2,
                    // Add bottom padding so last messages aren’t hidden behind the input bar
                    pb: 8,
                }}
            >
                {messages.length === 0 ? (
                    <Box sx={{color: "#ddd", fontSize: 14, textAlign: "center", height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", gap: "inherit"}}>
                        <div>Posez votre question privée à Nostra.</div>
                        <br/>
                        <br/>
                        <div>Nostra a accès à la partie, et voit ce que vous voyez, nul besoin de lui préciser l'état du jeu.</div>
                        <br/>
                        <br/>
                        <div>N'ayez pas peur, tout ce que vous demanderez ici, ainsi que ses réponses, restera entre lui et vous !</div>
                    </Box>
                ) : (
                    <>
                        {messages.map((m) => (
                            <Box
                                key={m.id}
                                sx={{
                                    display: "flex",
                                    justifyContent: m.author === "me" ? "flex-end" : "flex-start",
                                }}
                            >
                                <Box
                                    sx={{
                                        maxWidth: "80%",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        background: (theme) =>
                                            m.author === "me"
                                                ? theme.palette.primary.main
                                                : alpha(theme.palette.primary.dark, 0.9),
                                        color: "#fff",
                                        borderRadius: 2,
                                        padding: 1.5,
                                        fontSize: 14,
                                        boxShadow: 1,
                                        border: (theme) =>
                                            m.author === "me"
                                                ? `1px solid ${alpha(theme.palette.primary.light, 0.2)}`
                                                : `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                                    }}
                                >
                                    {m.text}
                                </Box>
                            </Box>
                        ))}

                        {isThinking && (
                            <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                                <Box
                                    aria-label="Nostra réfléchit"
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 1,
                                        background: (theme) => alpha(theme.palette.primary.dark, 0.15),
                                        color: "#fff",
                                        borderRadius: 2,
                                        padding: 1,
                                        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    }}
                                >
                                    <CircularProgress size={16} thickness={5} color="primary" />
                                    <span style={{ fontSize: 12, opacity: 0.9 }}>Nostra réfléchit…</span>
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </Box>

            {/* Chat input bar fixed at bottom */}
            <Box
                component="form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSend();
                }}
                sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 1,
                    padding: 1,
                    borderTop: "1px solid rgba(255,255,255,0.1)",
                    backdropFilter: "blur(4px)",
                    // Make the bar sticky so it stays at the bottom and harmonizes with scrolling
                    position: "sticky",
                    bottom: 0,
                    alignSelf: "stretch",
                }}
            >
                <TextField
                    aria-label="Message"
                    placeholder="Votre question"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    multiline
                    minRows={1}
                    maxRows={6}
                    fullWidth
                    InputProps={{
                        sx: {
                            color: "#fff",
                        },
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            background: "rgba(255,255,255,0.06)",
                        },
                    }}
                />
                <IconButton
                    aria-label="Envoyer"
                    color="primary"
                    onClick={handleSend}
                    disabled={!canSend}
                >
                    <SendIcon/>
                </IconButton>
            </Box>
        </Box>
    );
};

export default PrivateQuestion;
